/**
 * The sheet's words for what is sold: tickets or merch, as the purchase
 * says (`purchase.goods`). Nothing here names a seller.
 */

/** What is sold. */
export type Goods = "tickets" | "merch";

/** What a purchase calls what it sells, wherever it is bought (Link in
 *  Bio's sheet, the app's native and app-web sheets): an event's tickets,
 *  or a merch product's variants (its sizes, colours), which are chosen as
 *  tickets are. */
export type GoodsWords = {
  /** The access page's button that opens the purchase sheet. */
  buy: string;
  /** The purchase sheet's name, for assistive technology. */
  sheet: (title: string) => string;
  /** The first step's heading, for the names of the kinds on sale (the
   *  rows) and, for merch, what its store says they are chosen by (the
   *  purchase's option label, "Colour and size"). */
  choose: (names: readonly string[], optionLabel?: string | null) => string;
  /** The contact step's heading. */
  contact: string;
  /** Several of them, in the order's line ("3 tickets"). */
  many: string;
  /** Back from the checkout to the first step. */
  back: string;
  /** What the checkout's address is for: tickets go to it; merch is
   *  posted, so it only gets the order's emails; a download's link is
   *  emailed, so it gets the download. */
  destination: string;
  loading: string;
  failed: string;
  /** Nothing on sale to choose from. */
  none: string;
};

/** A variant's name that is a size: a letter size (XS, M, XXL, 3XL) or a
 *  number (10, UK 8), whatever its case. */
const SIZE = /^(?:[2-6]?X{0,4}[SL]|M|(?:(?:UK|EU|US) ?)?\d{1,2}(?:\.5)?)$/i;

/** Whether a name is a size (`SIZE`), its letters as a button may space
 *  them ("X L", Black Lounge's) read together. */
export const isSizeName = (name: string): boolean =>
  SIZE.test(name.trim()) || (/^[A-Za-z](?: [A-Za-z])+$/.test(name.trim()) && SIZE.test(name.replace(/\s+/g, "")));

const words: Readonly<Record<Goods, GoodsWords>> = {
  tickets: {
    buy: "Get tickets",
    sheet: (title) => `Tickets for ${title}`,
    choose: () => "Select tickets",
    contact: "Get your tickets",
    many: "tickets",
    back: "Back to tickets",
    destination: "Tickets destination",
    loading: "Loading tickets…",
    failed: "Could not load tickets.",
    none: "No tickets are available yet.",
  },
  merch: {
    buy: "Buy",
    sheet: (title) => `Buy ${title}`,
    choose: (names, optionLabel) =>
      names.length < 2
        ? "Choose how many"
        : optionLabel?.trim()
          ? `Choose your ${optionLabel.trim().toLowerCase()}`
          : names.every(isSizeName)
            ? "Choose your size"
            : "Choose an option",
    contact: "Your order",
    many: "items",
    back: "Back",
    destination: "Order emails to",
    loading: "Loading…",
    failed: "Could not load this item.",
    none: "This item isn't available right now.",
  },
};

/** A digital download's words (merch whose purchase's delivery is
 *  `emailed`): merch's, but its email gets the download itself. */
const download: GoodsWords = { ...words.merch, destination: "Download sent to" };

/** The words for what is sold (a purchase's `goods`); tickets when absent,
 *  as an engine event's are; merch its store emails (`emailed`, a digital
 *  download) says so. */
export const goodsWords = (goods: Goods | undefined, emailed = false): GoodsWords =>
  goods === "merch" && emailed ? download : words[goods ?? "tickets"];
